import { CanvasTexture, RepeatWrapping, Texture } from 'three';

/**
 * Procedural noise ground texture — subtle grain on a grey-blue base.
 * 512×512, repeats 4×4 across the ground plane.
 */
export function groundTexture(): Texture {
  const cached = cache.get('ground-texture');
  if (cached) return cached;

  if (typeof document === 'undefined') {
    // Test/SSR fallback: return a dummy texture
    return new Texture();
  }

  const SIZE = 512;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;

  // Base color: medium grey-blue
  ctx.fillStyle = '#3a4a55';
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Add subtle noise/grain
  const imageData = ctx.getImageData(0, 0, SIZE, SIZE);
  for (let i = 0; i < imageData.data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 20;
    imageData.data[i] = Math.max(0, Math.min(255, (imageData.data[i] ?? 0) + noise));
    imageData.data[i + 1] = Math.max(0, Math.min(255, (imageData.data[i + 1] ?? 0) + noise));
    imageData.data[i + 2] = Math.max(0, Math.min(255, (imageData.data[i + 2] ?? 0) + noise));
  }
  ctx.putImageData(imageData, 0, 0);

  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.repeat.set(4, 4);
  cache.set('ground-texture', tex);
  return tex;
}

const cache = new Map<string, Texture>();

/**
 * Generate a diamond mesh alphaMap procedurally.
 * White = solid, black = hole. Diamonds with rounded corners ~20mm pitch.
 */
export function diamondMeshAlphaMap(): Texture {
  const cached = cache.get('diamond-mesh-alpha');
  if (cached) return cached;

  if (typeof document === 'undefined') {
    // Test/SSR fallback: return a dummy texture
    return new Texture();
  }

  const SIZE = 256;
  const CELL = 32; // 8 diamonds per side
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;

  // White background (solid)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Black diamonds (holes)
  ctx.fillStyle = '#000000';
  const HALF = CELL / 2;
  const HOLE_R = CELL * 0.3; // 30% of cell = hole size
  for (let y = HALF; y < SIZE; y += CELL) {
    const offsetX = (Math.floor(y / CELL) % 2) * HALF; // brick pattern
    for (let x = HALF + offsetX; x < SIZE; x += CELL) {
      ctx.beginPath();
      ctx.moveTo(x, y - HOLE_R);
      ctx.lineTo(x + HOLE_R, y);
      ctx.lineTo(x, y + HOLE_R);
      ctx.lineTo(x - HOLE_R, y);
      ctx.closePath();
      ctx.fill();
    }
  }

  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  cache.set('diamond-mesh-alpha', tex);
  return tex;
}

/**
 * Rib pattern normalMap — parallel ribs along Z direction for floor plank texture.
 */
export function ribPatternNormalMap(): Texture {
  const cached = cache.get('rib-pattern-normal');
  if (cached) return cached;

  if (typeof document === 'undefined') {
    // Test/SSR fallback: return a dummy texture
    return new Texture();
  }

  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;

  // Base normal = (0.5, 0.5, 1.0) → rgb(128, 128, 255)
  ctx.fillStyle = 'rgb(128, 128, 255)';
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Ribs running along Y axis (vertical lines) → normal perturbation in X
  const RIB_WIDTH = 8;
  const RIB_PITCH = 16;
  for (let x = 0; x < SIZE; x += RIB_PITCH) {
    // Highlight side of rib: normal toward +X → rgb(180, 128, 230)
    ctx.fillStyle = 'rgb(180, 128, 230)';
    ctx.fillRect(x, 0, RIB_WIDTH / 2, SIZE);
    // Shadow side of rib: normal toward -X → rgb(76, 128, 230)
    ctx.fillStyle = 'rgb(76, 128, 230)';
    ctx.fillRect(x + RIB_WIDTH / 2, 0, RIB_WIDTH / 2, SIZE);
  }

  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  cache.set('rib-pattern-normal', tex);
  return tex;
}
